import React, { useState, useRef, useEffect } from 'react';
import { Share2, Twitter, Facebook, Link2, Check } from 'lucide-react';
import { toast } from 'react-toastify';

const ShareButton = ({ url, text, label = 'SHARE', size = 'sm' }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fullUrl = url.startsWith('http') ? url : `${window.location.origin}${url}`;
  const encodedUrl = encodeURIComponent(fullUrl);
  const encodedText = encodeURIComponent(text || '');

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      toast.success('Link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy link');
    }
    setOpen(false);
  };

  const openTwitter = () => {
    window.open(
      `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
      '_blank',
      'noopener,noreferrer,width=550,height=420'
    );
    setOpen(false);
  };

  const openFacebook = () => {
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      '_blank',
      'noopener,noreferrer,width=580,height=400'
    );
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 bg-[#1a1a1a] hover:bg-[#2a2a2a] text-gray-400 hover:text-white font-mono tracking-wider transition-colors ${
          size === 'xs' ? 'text-[10px] px-2 py-1' : 'text-xs px-3 py-1.5'
        }`}
        title="Share"
        data-testid="share-btn"
      >
        <Share2 className={size === 'xs' ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
        {label}
      </button>

      {open && (
        <div className="absolute right-0 bottom-full mb-1 w-44 bg-[#111] border border-[#2a2a2a] rounded shadow-xl z-50">
          <button
            onClick={openTwitter}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 text-xs text-gray-300 hover:bg-[#1a1a1a] hover:text-white transition-colors"
          >
            <svg className="w-3.5 h-3.5 fill-current text-gray-300" viewBox="0 0 24 24">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
            Post on X
          </button>
          <button
            onClick={openFacebook}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 text-xs text-gray-300 hover:bg-[#1a1a1a] hover:text-white transition-colors"
          >
            <Facebook className="w-3.5 h-3.5 text-blue-500" />
            Share on Facebook
          </button>
          <button
            onClick={copyLink}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 text-xs text-gray-300 hover:bg-[#1a1a1a] hover:text-white transition-colors border-t border-[#2a2a2a]"
          >
            {copied
              ? <Check className="w-3.5 h-3.5 text-green-400" />
              : <Link2 className="w-3.5 h-3.5 text-gray-400" />}
            {copied ? 'Copied!' : 'Copy Link'}
          </button>
        </div>
      )}
    </div>
  );
};

export default ShareButton;
